import { ApiError, CertificateStatus } from '../../api';

/**
 * The words the certificate screen uses, and the one distinction it has to make.
 *
 * A lookup module like [`mailbox`](./mailbox.ts), and here for the same reason:
 * a status appears in the table, in the summary above it and in the sentence
 * that explains why an upload was refused, and "vencido" spelled three ways
 * across them is how a screen stops being trusted.
 *
 * The screen is deliberately blunt about expiry. An A1 certificate lasts a year
 * and is replaced by somebody who has to buy the next one from a certification
 * authority, which takes days -- so the point of this screen is not that it
 * lists certificates, it is that a month before the deadline it says so loudly
 * enough that somebody acts.
 */

export const CERTIFICATES_COPY = {
  title: 'Certificado digital A1',
  description:
    'O certificado que a plataforma usa para assinar o que envia ao eSocial. É uma credencial ' +
    'nossa perante terceiros, e não de um cliente: cada certificado vale por um CNPJ, e mais de ' +
    'um pode conviver aqui.',

  uploadTitle: 'Instalar certificado',
  uploadDescription:
    'O arquivo .pfx ou .p12 e a senha que o abre. O arquivo é aberto antes de qualquer coisa ' +
    'ser guardada: se a senha estiver errada, ou se o arquivo não for um PKCS#12, nada é criado.',
  file: 'Arquivo do certificado',
  fileHint: 'O .pfx ou .p12 emitido pela autoridade certificadora.',
  password: 'Senha do certificado',
  /**
   * Said on the screen and not only in the API documentation. Somebody who
   * believed the password could be looked up later would keep it nowhere else,
   * and the day the certificate has to be reinstalled it would be gone.
   */
  passwordHint:
    'Guardada cifrada e nunca devolvida por nenhuma tela ou endpoint. Não há como consultá-la ' +
    'depois: guarde-a também no cofre de senhas da equipe.',
  submit: 'Instalar certificado',
  submitting: 'Instalando…',
  back: 'Voltar para a lista de certificados',
  cancel: 'Cancelar',
  installed: 'Certificado instalado.',
  replaced: 'Certificado instalado. O anterior deste CNPJ foi marcado como substituído.',

  listTitle: 'Certificados instalados',
  listDescription:
    'Inclusive os substituídos, desativados e vencidos: "com qual certificado assinamos em ' +
    'abril?" é uma pergunta sobre um certificado que já não vale. Nada é apagado aqui.',
  listCaption: 'Certificados digitais A1 instalados',
  empty: 'Nenhum certificado instalado',
  emptyDescription:
    'A plataforma ainda não tem com que assinar envios ao eSocial. Instale o .pfx em ' +
    '"Instalar certificado".',
  loading: 'Carregando certificados…',
  loadError: 'Não foi possível carregar os certificados',

  holder: 'Titular',
  cnpj: 'CNPJ',
  validity: 'Validade',
  issuer: 'Autoridade certificadora',
  fingerprint: 'Impressão digital (SHA-256)',
  fingerprintHint: 'Compare com o que o portal da autoridade certificadora mostra.',
  status: 'Situação',
  uploadedBy: 'Instalado por',
  actions: 'Ações',
  deactivate: 'Desativar',
  deactivating: 'Desativando…',
  deactivated: 'Certificado desativado.',
  deactivateFailed: 'Não foi possível desativar o certificado.',
  /**
   * Asked before, because it is the one action here that cannot be undone by
   * uploading something: desativar sem substituto deixa o CNPJ sem certificado.
   */
  deactivateConfirm:
    'Desativar este certificado? A plataforma deixa de assinar por este CNPJ até que outro ' +
    'certificado seja instalado, e não há como desfazer.',

  /** The banner above the table, when something needs a human this month. */
  attentionExpiring: (count: number) =>
    count === 1
      ? 'Um certificado vence nos próximos 30 dias. Providencie a renovação.'
      : `${count} certificados vencem nos próximos 30 dias. Providencie a renovação.`,
  attentionExpired: (count: number) =>
    count === 1
      ? 'Um certificado venceu. A plataforma não assina mais por esse CNPJ.'
      : `${count} certificados venceram. A plataforma não assina mais por esses CNPJs.`,
} as const;

/** Where one certificate is in its life. */
export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  [CertificateStatus.ACTIVE]: 'Válido',
  [CertificateStatus.EXPIRING_SOON]: 'Vence em breve',
  [CertificateStatus.EXPIRED]: 'Vencido',
  [CertificateStatus.SUPERSEDED]: 'Substituído',
  [CertificateStatus.DEACTIVATED]: 'Desativado',
};

/**
 * Which statuses are a problem somebody has to act on.
 *
 * `superseded` and `deactivated` are deliberately not here: both are the result
 * of somebody deciding something, and flagging a decision as a warning is how a
 * screen trains people to ignore its warnings.
 */
export const CERTIFICATE_STATUS_TONES: Record<CertificateStatus, 'ok' | 'warning' | 'blocking'> = {
  [CertificateStatus.ACTIVE]: 'ok',
  [CertificateStatus.EXPIRING_SOON]: 'warning',
  [CertificateStatus.EXPIRED]: 'blocking',
  [CertificateStatus.SUPERSEDED]: 'ok',
  [CertificateStatus.DEACTIVATED]: 'ok',
};

/** The statuses that mean somebody has to buy a new certificate. */
export const NEEDS_ATTENTION: readonly CertificateStatus[] = [
  CertificateStatus.EXPIRING_SOON,
  CertificateStatus.EXPIRED,
];

/**
 * Why an upload was refused, in pt-BR, keyed on what the server said is at fault.
 *
 * The four refusals are genuinely different actions for the person at the
 * screen -- pick another file, re-type the password, ask the authority for the
 * archive again, ask them for an e-CNPJ instead -- and the generic sentence the
 * error code maps to says none of them.
 *
 * Keyed on `details[].field`, which the API documents as exactly this: `file`,
 * `password`, `certificate` or `subject_cnpj`. Not on the server's own message,
 * which is written in English for whoever is integrating, and not on the HTTP
 * status, which cannot tell the last three apart.
 */
const REFUSAL_BY_FIELD: Record<string, string> = {
  file: 'O arquivo não é um certificado PKCS#12. Envie o .pfx ou .p12 emitido pela autoridade certificadora.',
  password:
    'A senha não abre este certificado. Confira a senha — e, se ela estiver certa, se o arquivo é mesmo o que a autoridade certificadora emitiu.',
  certificate:
    'O arquivo não traz a chave privada e o certificado juntos, então não serve para assinar. Peça o arquivo completo à autoridade certificadora.',
  subject_cnpj:
    'O titular do certificado não tem CNPJ, então não é um e-CNPJ e não pode assinar envios ao eSocial. Um e-CPF não serve aqui.',
};

/** The sentence to show for a refused upload, or `null` to use the default. */
export function refusalMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) {
    return null;
  }
  for (const detail of error.details) {
    const message = detail.field === null ? undefined : REFUSAL_BY_FIELD[detail.field ?? ''];
    if (message !== undefined) {
      return message;
    }
  }
  return null;
}
